import { useEffect, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RealtimeProvider } from "@upstash/realtime/client";
import { useOutcome } from "@pinatagrams/fiesta";

import { useRealtime } from "#/hooks/use-realtime";
import { useRealtimeStore } from "#/stores/realtime-store";
import { apiUrl } from "#/utils/api";

function RealtimeBridge() {
	const { emit } = useOutcome();
	const queryClient = useQueryClient();
	const setStatus = useRealtimeStore((state) => state.setStatus);
	const { status } = useRealtime({
		events: [
			"notification.alert",
			"collections.update.started",
			"collections.update.queued",
			"collections.update.finished",
			"collections.update.processing",
			"collections.update.processed",
			"collections.update.completed",
			"collections.update.failed",
		],
		onData: ({ event, data }) => {
			// Invalidate logs query on any collection update event
			if (event.startsWith("collections.update.")) {
				void queryClient.invalidateQueries({
					predicate: (query) => query.queryKey[0] === "logs" || query.queryKey[0] === "collections",
				});
				return;
			}

			if (event === "notification.alert") {
				emit({
					origin: { id: "realtime-notification" },
					impact: "permissive",
					intent: "inform",
					title: "Notification",
					message: JSON.stringify(data),
				});
			}
		},
	});

	useEffect(() => {
		setStatus(status ?? "idle");
	}, [setStatus, status]);

	return null;
}

/**
 * Provider wrapper that mounts the realtime bridge and supplies the client context.
 */
export function RealtimeWrapper({ children }: { children: ReactNode }) {
	const realtimeUrl = apiUrl("/realtime");

	return (
		<RealtimeProvider api={{ url: realtimeUrl, withCredentials: false }} maxReconnectAttempts={3}>
			<RealtimeBridge />
			{children}
		</RealtimeProvider>
	);
}
